import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingState, InputNumber } from "components";
import { usePaymentVoucher } from "hooks";
import { PaymentVoucherForm, PaymentVoucherItem, PaymentVoucherRow } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { debounce } from "utils/helpers/data_helpers";

const todayString = () => new Date().toISOString().slice(0, 10);
const ATTACHMENT_SPLIT_REGEX = /[,\n]/;

const defaultFormValues: PaymentVoucherForm = {
  payment_date: todayString(),
  object_name: "",
  receiver_name: "",
  address: "",
  description: "",
  attachments: [],
  original_voucher_no: "",
  import_invoice_id: undefined,
  items: [],
};

const normalizeNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "string"
          ? item
          : String(item ?? ""),
      )
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch (error) {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeItems = (items?: PaymentVoucherItem[]): PaymentVoucherItem[] =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const normalizedAmount = normalizeNumber(item.amount);
          const partnerIdValue =
            item.partner_id !== undefined &&
            item.partner_id !== null &&
            String(item.partner_id).trim() !== "" &&
            Number.isFinite(Number(item.partner_id))
              ? Number(item.partner_id)
              : undefined;

          return {
            amount: normalizedAmount,
            ...(partnerIdValue !== undefined ? { partner_id: partnerIdValue } : {}),
            reason: item.reason ? String(item.reason).trim() : undefined,
            description: item.description
              ? String(item.description).trim()
              : undefined,
          };
        })
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            Boolean(item.reason) ||
            Boolean(item.description),
        )
    : [];

const PaymentVoucherDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createPaymentVoucher, updatePaymentVoucher } =
    usePaymentVoucher();

  const {
    data: detailResponse,
    isFetching: isFetchingDetail,
  } = fetchDetail(id ?? "", isEdit);

  const [form, setForm] = useState<PaymentVoucherForm>(defaultFormValues);
  const [attachmentsInput, setAttachmentsInput] = useState<string[]>([]);
  const [attachmentsText, setAttachmentsText] = useState<string>("");
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [items, setItems] = useState<PaymentVoucherItem[]>([]);

  useEffect(() => {
    if (!detailResponse?.data) return;
    const voucher = detailResponse.data as PaymentVoucherRow;
    setForm({
      payment_date: voucher.payment_date || todayString(),
      object_name: voucher.object_name || "",
      receiver_name: voucher.receiver_name || "",
      address: voucher.address || "",
      description: voucher.description || "",
      attachments: normalizeAttachments(voucher.attachments),
      original_voucher_no: voucher.original_voucher_no || "",
      import_invoice_id: voucher.import_invoice_id,
      items: sanitizeItems(voucher.items),
    });
    const normalizedAttachments = normalizeAttachments(voucher.attachments);
    setExistingAttachments(normalizedAttachments);
    setAttachmentsInput(normalizedAttachments);
    setAttachmentsText(normalizedAttachments.join(", "));
    setItems(
      sanitizeItems(voucher.items).length
        ? sanitizeItems(voucher.items)
        : [{ amount: 0, partner_id: "", reason: "", description: "" }],
    );
  }, [detailResponse]);

  useEffect(() => {
    if (isEdit) return;
    setForm(defaultFormValues);
    setItems([{ amount: 0, partner_id: "", reason: "", description: "" }]);
    setAttachmentsInput([]);
    setAttachmentsText("");
    setExistingAttachments([]);
  }, [isEdit]);

  const totalAmount = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + (Number(item.amount ?? 0) || 0),
        0,
      ),
    [items],
  );

  const handleFormChange = useCallback(
    (key: keyof PaymentVoucherForm, value: any) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleAddItem = useCallback(() => {
    setItems((prev) => [
      ...prev,
      { amount: 0, partner_id: "", reason: "", description: "" },
    ]);
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof PaymentVoucherItem, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                [key]: key === "amount" ? normalizeNumber(value) : value,
              }
            : item,
        ),
      );
    },
    [],
  );
  const debouncedItemChange = useMemo(
    () =>
      debounce(
        (index: number, key: keyof PaymentVoucherItem, value: any) =>
          handleItemChange(index, key, value),
        200,
      ),
    [handleItemChange],
  );
  const debouncedAttachmentsParse = useMemo(
    () =>
      debounce((text: string) => {
        const parsed = text
          .split(ATTACHMENT_SPLIT_REGEX)
          .map((v) => v.trim())
          .filter(Boolean);
        setAttachmentsInput(parsed);
      }, 250),
    [],
  );

  useEffect(() => {
    debouncedAttachmentsParse(attachmentsText);
  }, [attachmentsText, debouncedAttachmentsParse]);

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback((): PaymentVoucherForm | null => {
    if (!form.payment_date || !form.object_name || !form.receiver_name) {
      SnackbarUtils.error("Ngày chi, Đối tượng và Người nộp là bắt buộc");
      return null;
    }

    const normalizedItems = sanitizeItems(items);

    if (!normalizedItems.length || !normalizedItems[0].amount) {
      SnackbarUtils.error("Vui lòng nhập ít nhất một dòng chi tiết và số tiền");
      return null;
    }

    return {
      ...form,
      attachments:
        attachmentsInput.length > 0
          ? normalizeAttachments(attachmentsInput)
          : existingAttachments,
      items: normalizedItems,
    };
  }, [attachmentsInput, existingAttachments, form, items]);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      const payload = buildPayload();
      if (!payload) return;

      if (isEdit && id) {
        updatePaymentVoucher.mutate(
          { id, ...payload },
          {
            onSuccess: (res) => {
              if (res?.data) {
                SnackbarUtils.success("Cập nhật phiếu chi thành công");
              }
              navigate(ROUTES.ACCOUNTING.CASH_PAYMENT);
            },
          },
        );
        return;
      }

      createPaymentVoucher.mutate(payload, {
        onSuccess: () => {
          navigate(ROUTES.ACCOUNTING.CASH_PAYMENT);
        },
      });
    },
    [
      buildPayload,
      createPaymentVoucher,
      id,
      isEdit,
      navigate,
      updatePaymentVoucher,
    ],
  );

  const isSaving =
    createPaymentVoucher.isPending || updatePaymentVoucher.isPending;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase">Phiếu chi</p>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEdit ? "Cập nhật phiếu chi" : "Thêm phiếu chi"}
          </h1>
          <p className="text-sm text-gray-600">
            Nhập thông tin chung và danh sách dòng chi tiết như mẫu.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.ACCOUNTING.CASH_PAYMENT)}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Danh sách phiếu chi
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
            disabled={isSaving}
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Đang lưu..." : "Lưu phiếu chi"}
          </button>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-base font-semibold text-gray-900">
              Thông tin chung
            </h2>
          </div>
          <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Đối tượng *
              </label>
              <input
                value={form.object_name}
                onChange={(e) => handleFormChange("object_name", e.target.value)}
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Khách lẻ"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Người nộp *
              </label>
              <input
                value={form.receiver_name}
                onChange={(e) =>
                  handleFormChange("receiver_name", e.target.value)
                }
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Nhập người nộp"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Ngày chi *
              </label>
              <input
                type="date"
                value={form.payment_date}
                onChange={(e) =>
                  handleFormChange("payment_date", e.target.value)
                }
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Số phiếu
              </label>
              <input
                value={form.original_voucher_no ?? ""}
                onChange={(e) =>
                  handleFormChange("original_voucher_no", e.target.value)
                }
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="TM00001"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Địa chỉ
              </label>
              <input
                value={form.address ?? ""}
                onChange={(e) => handleFormChange("address", e.target.value)}
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Nhập địa chỉ"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Kèm theo (nhiều tệp)
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={attachmentsText}
                  onChange={(e) => {
                    setAttachmentsText(e.target.value);
                  }}
                  placeholder="tệp1.png, tệp2.pdf"
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
                {(attachmentsInput.length > 0 || existingAttachments.length > 0) && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-xs font-semibold text-gray-600 mb-1">
                      Tệp đã chọn:
                    </p>
                    <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside">
                      {(attachmentsInput.length > 0 ? attachmentsInput : existingAttachments).map(
                        (item, idx) => <li key={`${idx}`}>{item}</li>,
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-gray-700">
                Diễn giải
              </label>
              <textarea
                value={form.description ?? ""}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
                rows={2}
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Nội dung chi"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Số chứng từ gốc
              </label>
              <input
                type="number"
                value={form.import_invoice_id ?? ""}
                onChange={(e) =>
                  handleFormChange(
                    "import_invoice_id",
                    e.target.value ? Number(e.target.value) : undefined,
                  )
                }
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="Nhập số chứng từ gốc"
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 className="text-base font-semibold text-gray-900">
              Thông tin chi tiết
            </h2>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-gray-500">Tổng tiền</p>
                <p className="text-base font-semibold text-gray-900">
                  {totalAmount.toLocaleString("vi-VN")} đ
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
                Thêm dòng
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    Số tiền
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Khách hàng/NCC
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Lý do chi
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    Diễn giải
                  </th>
                  <th className="px-3 py-2 text-center font-semibold">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {items.length ? (
                  items.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        <InputNumber
                          value={item.amount ?? 0}
                          onValueChange={(values) =>
                            debouncedItemChange(
                              index,
                              "amount",
                              values.floatValue ?? 0,
                            )
                          }
                          className="h-9 px-2 py-1 text-right text-sm"
                          suffix=" ₫"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        <input
                          value={item.partner_id ?? ""}
                          onChange={(e) =>
                            debouncedItemChange(index, "partner_id", e.target.value)
                          }
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Mã đối tác"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        <input
                          value={item.reason ?? ""}
                          onChange={(e) =>
                            debouncedItemChange(index, "reason", e.target.value)
                          }
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Lý do chi"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        <input
                          value={item.description ?? ""}
                          onChange={(e) =>
                            debouncedItemChange(index, "description", e.target.value)
                          }
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          placeholder="Diễn giải"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                          disabled={items.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chưa có dòng chi tiết. Thêm mới để bắt đầu.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-sm text-gray-500">
              Tổng cộng:{" "}
              <span className="font-semibold text-gray-900">
                {totalAmount.toLocaleString("vi-VN")} đ
              </span>
            </p>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Đang lưu..." : "Lưu phiếu chi"}
            </button>
          </div>
        </div>
      </form>

      {(isFetchingDetail || isSaving) && (
        <LoadingState
          message={isEdit ? "Đang tải phiếu chi" : "Đang lưu phiếu chi"}
          description="Vui lòng chờ trong giây lát."
        />
      )}
    </div>
  );
};

export default PaymentVoucherDetail;
